import React from 'react';
import { ArrowRight, MapPin, Move, Route as RouteIcon, ShieldCheck } from 'lucide-react';
import Button from '../ui/Button';
import { formatDistance, haversineMeters } from '../../utils/routeManagementHelpers';

/*
 * Confirmation for repositioning a stop from inside a route.
 *
 * Centred rather than tucked against the edge of the map, because this is a
 * decision the operator has to make, not a status message to glance at.
 *
 * The backdrop is deliberately light and only faintly blurred: the map stays
 * readable behind the dialog, so the dropped pin can still be seen while the
 * choice is being made. A heavy scrim would hide the very thing being confirmed.
 *
 * The dialog states the consequence plainly — this route only, stop master
 * untouched — because that is exactly the distinction that is easy to get wrong.
 */
export default function ConfirmStopMoveDialog({ pending, routeLabel, saving, onCancel, onConfirm }) {
  const dialogRef = React.useRef(null);

  // Escape cancels, as it does in every other dialog in the app.
  React.useEffect(() => {
    if (!pending) return undefined;
    function onKey(event) {
      if (event.key === 'Escape' && !saving) onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pending, saving, onCancel]);

  // Move focus into the dialog so the keyboard lands somewhere sensible.
  React.useEffect(() => {
    if (pending) dialogRef.current?.focus();
  }, [pending]);

  if (!pending) return null;

  const moved = haversineMeters(
    { lat: pending.prevLat, lng: pending.prevLng },
    { lat: pending.lat, lng: pending.lng }
  );

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-slate-900/25 backdrop-blur-[1px] animate-fade-in">
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="stop-move-title"
        className="w-[440px] max-w-full bg-white rounded-2xl shadow-pop border border-slate-200 overflow-hidden animate-slide-up focus:outline-none"
      >
        {/* Header */}
        <div className="flex items-start gap-3 px-5 pt-5 pb-4">
          <span className="h-10 w-10 shrink-0 rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 text-white flex items-center justify-center shadow-soft">
            <Move className="h-4.5 w-4.5" />
          </span>
          <div className="min-w-0">
            <h3 id="stop-move-title" className="text-[16px] font-semibold text-slate-900 leading-tight">
              Move this stop?
            </h3>
            <p className="text-[12.5px] text-slate-500 mt-0.5 truncate">
              <span className="font-medium text-slate-700">{pending.stopName}</span>
              {routeLabel ? (
                <>
                  {' '}on <span className="font-medium text-slate-700">{routeLabel}</span>
                </>
              ) : null}
            </p>
          </div>
        </div>

        {/* Before → after */}
        <div className="mx-5 rounded-xl border border-slate-200 bg-slate-50/80 p-3.5">
          <div className="flex items-center gap-2.5">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">From</p>
              <p className="font-mono text-[12px] text-slate-500 mt-1 tabular-nums truncate line-through">
                {Number(pending.prevLat).toFixed(6)}, {Number(pending.prevLng).toFixed(6)}
              </p>
            </div>
            <ArrowRight className="h-4 w-4 text-slate-300 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600">To</p>
              <p className="font-mono text-[12px] font-medium text-slate-900 mt-1 tabular-nums truncate">
                {Number(pending.lat).toFixed(6)}, {Number(pending.lng).toFixed(6)}
              </p>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-slate-200/80 flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 text-amber-600 shrink-0" />
            <span className="text-[12px] text-slate-600">
              Moved <span className="font-semibold text-slate-900">{formatDistance(moved)}</span>
            </span>
          </div>
        </div>

        {/* What this does and does not change */}
        <div className="mx-5 mt-3 rounded-xl border border-emerald-200 bg-emerald-50/70 px-3.5 py-3 flex items-start gap-2.5">
          <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-[12px] leading-relaxed text-emerald-900">
            <p className="font-semibold">Saved on this route only</p>
            <p className="text-emerald-800/90 mt-0.5">
              The stop master keeps its own position, and other routes using this stop
              are not affected.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 px-5 py-4 mt-4 bg-slate-50/60 border-t border-slate-100">
          <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400">
            <RouteIcon className="h-3 w-3" />
            Route position
          </span>
          <div className="flex-1" />
          <Button variant="ghost" size="md" onClick={onCancel} disabled={saving}>
            Discard
          </Button>
          <Button variant="primary" size="md" loading={saving} onClick={onConfirm}>
            {saving ? 'Saving' : 'Save position'}
          </Button>
        </div>
      </div>
    </div>
  );
}
